import React, { useContext, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { InactivityContext } from "../contexts/InactivityContext";
import AdminLogin from "./adminLogin";
import AdminTerms from "./adminTerms";
import AdminRegister from "./adminRegister";
import ManageUser from "./manageUser";
import ManageStore from "./manageStore";
import AccessCodes from "./accessCodes";
import Quiz from "./quiz";
import StoresReport from "./storesReport";
import UsersReport from "./usersReport";
import UsersDataReport from "./usersDataReport";
import ManageVideos from "./manageVideos";
import ForgotPassword from "./forgotPassword";
import ResetTokenComponent from "./resetPassword";
import VerifyAdmin from "./verifyAdmin";
import ManageAdmin from "./manageAdmin";
import EducationManagement from "./educationManagement";
import NotificationManagement from "./notificationManagement";
import ManageStocks from "./manageStocks";
import ManageIvts from "./manageIvts";
import ManageStocksKiosk from "./manageKioskStocks";
import SupportModule from "./support";
import ManageProductCatalouge from "./manageProductCatalouge";
import ManageConfiguration from "./manageConfiguration";
import ManageOrders from "./manageOrders";
import PhysicalCardBulkUpload from "./physicalCardBulkUpload";
import PhysicalCardReport from "./physicalCardReport";
import LmsOverview from "./lmsOverview";
import LmsCourses from "./lmsCourses";
import LmsCourseWizard from "./lmsCourseWizard";
import LmsCoursePreview from "./lmsCoursePreview";
import LmsRegistrations from "./lmsRegistrations";
import LmsLearners from "./lmsLearners";
import LmsLearnerDetail from "./lmsLearnerDetail";
import LmsSettings from "./lmsSettings";
import LmsNewsletter from "./lmsNewsletter";
import LmsCourseDetail from "./lmsCourseDetail";
import {
  PortalAuthGuard,
  PortalNotFound,
  PortalPublicOnly,
} from "../_shared/routeGuards";
import { AdminCapabilityGuard, AdminHomeRedirect } from "./adminRouteGuards";

const AdminRouter = () => {
  const { setTimeoutValue } = useContext(InactivityContext);
  const location = useLocation();

  // Initialize timeout value when component mounts or route changes
  useEffect(() => {
    const timeout = localStorage?.getItem("adminTimeout");
    if (timeout) {
      const timeoutMs = parseFloat(timeout) * 60 * 1000; // Convert minutes to milliseconds
      setTimeoutValue(timeoutMs);
    }
  }, [setTimeoutValue, location.pathname]);

  return (
    <Routes>
      <Route
        path="login"
        element={
          <PortalPublicOnly role="admin" homePath="/admin">
            <AdminLogin />
          </PortalPublicOnly>
        }
      />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-token/:id" element={<ResetTokenComponent />} />
      <Route path="terms" element={<AdminTerms />} />
      <Route path="register" element={<AdminRegister />} />
      <Route path="activate-account" element={<VerifyAdmin />} />

      <Route element={<PortalAuthGuard loginPath="/admin/login" role="admin" />}>
        <Route index element={<AdminHomeRedirect />} />

        <Route element={<AdminCapabilityGuard capability="core" />}>
          <Route path="manage-admins" element={<ManageAdmin />} />
          <Route path="manage-users" element={<ManageUser />} />
          <Route path="manage-product-catalouge" element={<ManageProductCatalouge />} />
          <Route path="manage-stores" element={<ManageStore />} />
          <Route path="manage-stores/:id" element={<ManageStocks />} />
          <Route path="access-codes" element={<AccessCodes />} />
          <Route path="quiz-videos" element={<ManageVideos />} />
          <Route path="quiz" element={<Quiz />} />
          <Route path="venues-report" element={<StoresReport />} />
          <Route path="users-report" element={<UsersReport />} />
          <Route path="users-data-report" element={<UsersDataReport />} />
          <Route path="education-management" element={<EducationManagement />} />
          <Route path="notification-management" element={<NotificationManagement />} />
          <Route path="manage-kiosk" element={<ManageIvts />} />
          <Route path="manage-online-store" element={<ManageOrders />} />
          <Route path="manage-kiosk/:id" element={<ManageStocksKiosk />} />
          <Route path="support" element={<SupportModule />} />
          <Route path="manage-configuration" element={<ManageConfiguration />} />
        </Route>

        <Route element={<AdminCapabilityGuard capability="physicalCard" />}>
          <Route path="physical-cards" element={<PhysicalCardBulkUpload />} />
          <Route path="physical-cards-report" element={<PhysicalCardReport />} />
        </Route>

        <Route element={<AdminCapabilityGuard capability="lms" />}>
          <Route path="lms/overview" element={<LmsOverview />} />
          <Route path="lms/courses" element={<LmsCourses />} />
          <Route path="lms/course" element={<Navigate to="/admin/lms/course/new" replace />} />
          <Route path="lms/course/new" element={<LmsCourseWizard />} />
          <Route path="lms/course/:id/preview" element={<LmsCoursePreview />} />
          <Route path="lms/course/:id" element={<LmsCourseWizard />} />
          <Route path="lms/registrations" element={<LmsRegistrations />} />
          <Route path="lms/learners" element={<LmsLearners />} />
          <Route path="lms/learners/:learnerId" element={<LmsLearnerDetail />} />
          <Route path="lms/settings" element={<LmsSettings />} />
          <Route path="lms/newsletter" element={<LmsNewsletter />} />
          <Route path="lms/course/detail/:id" element={<LmsCourseDetail />} />
        </Route>

        <Route
          path="*"
          element={<PortalNotFound homePath="/admin" portalName="admin" />}
        />
      </Route>
    </Routes>
  );
};

export default AdminRouter;
